import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Alert,
  AlertTitle,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { SubmissionStatusFieldsFragment } from '@libs/graphql-types';

const useStyles = makeStyles((theme: Theme) => ({
  statusContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {},
  errorDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    '& pre': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      margin: 0,
    },
  },
  apiResponse: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    '& pre': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      margin: 0,
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadingProgressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
}));

interface ApplicationStatusDisplayProps {
  data?: SubmissionStatusFieldsFragment | null;
  loading?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'SUBMITTED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'info';
    case 'FAILED':
    case 'ERROR':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'SUBMITTED':
      return 'Successfully Submitted';
    case 'PENDING':
      return 'Pending Submission';
    case 'PROCESSING':
      return 'Processing...';
    case 'FAILED':
    case 'ERROR':
      return 'Submission Failed';
    default:
      return status || 'Unknown Status';
  }
};

export const ApplicationStatusDisplay: React.FC<
  ApplicationStatusDisplayProps
> = ({ data, loading = false }) => {
  const classes = useStyles();

  if (!data) {
    return null;
  }

  const { submission_status, error_message, api_response } = data;

  const hasError = error_message && error_message.trim() !== '';
  const isSubmitted = submission_status?.toUpperCase() === 'SUBMITTED';

  return (
    <Box className={classes.statusContainer} position="relative">
      {/* Subtle loading indicator at the top when refetching */}
      {/* {loading && (
        <LinearProgress
          className={classes.loadingProgressBar}
          color="info"
          variant="indeterminate"
        />
      )} */}

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" component="h3">
          Application Status
        </Typography>
        {submission_status && (
          <Chip
            label={getStatusLabel(submission_status)}
            color={getStatusColor(submission_status)}
            variant={isSubmitted ? 'filled' : 'outlined'}
            className={classes.statusChip}
          />
        )}
      </Box>

      {isSubmitted && (
        <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
          <AlertTitle>Application Successfully Submitted</AlertTitle>
          Your candidate application has been successfully submitted to the VMS
          system.
        </Alert>
      )}

      {hasError && (
        <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
          <AlertTitle>Submission Error</AlertTitle>
          There was an error submitting your application. Please review the
          details below.
          <Box className={classes.errorDetails}>
            <Typography variant="body2" component="div">
              <strong>Error Details:</strong>
            </Typography>
            <pre>{error_message}</pre>
          </Box>
        </Alert>
      )}

      {api_response && (
        <Accordion
          defaultExpanded={false}
          elevation={0}
          sx={{
            mt: 2,
            boxShadow: 'none',
            backgroundColor: 'transparent',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="api-response-content"
            id="api-response-header"
          >
            <Typography variant="body2" component="div">
              <strong>API Response</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className={classes.apiResponse}>
              <pre>
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(api_response), null, 2);
                  } catch {
                    return api_response;
                  }
                })()}
              </pre>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default ApplicationStatusDisplay;
